// Client facade for address autocomplete + place-details resolution.
// Calls server proxies so Google API keys never enter the bundle.

import type {
  AddressAutocompletePick,
  AddressAutocompleteSuggestion,
} from "./addressAutocomplete/types";
import type { StructuredAddress } from "./addressValidation/types";

export type { AddressAutocompletePick, AddressAutocompleteSuggestion };

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const resp = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await resp.json().catch(() => ({}))) as T & { error?: string };
  if (!resp.ok) {
    throw new Error(
      typeof (data as { error?: string }).error === "string"
        ? (data as { error: string }).error
        : `Address request failed (${resp.status})`,
    );
  }
  return data;
}

export async function fetchAddressAutocomplete(args: {
  query: string;
  sessionToken?: string;
}): Promise<{ provider: string; suggestions: AddressAutocompleteSuggestion[] }> {
  const data = await postJson<{
    provider: string;
    suggestions: AddressAutocompleteSuggestion[];
  }>("/api/address/autocomplete", {
    query: args.query,
    sessionToken: args.sessionToken,
  });
  return {
    provider: data.provider ?? "unknown",
    suggestions: data.suggestions ?? [],
  };
}

export async function resolveAddressPlaceDetails(args: {
  placeId: string;
  sessionToken?: string;
}): Promise<AddressAutocompletePick> {
  const data = await postJson<{
    normalized: StructuredAddress;
    formatted: string;
    provider: string;
  }>("/api/address/place-details", {
    placeId: args.placeId,
    sessionToken: args.sessionToken,
  });
  return {
    placeId: args.placeId,
    label: data.formatted,
    normalized: data.normalized,
    formatted: data.formatted,
  };
}

/** Stable per-form session token (Google billing optimization). */
export function createAddressAutocompleteSessionToken(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `addr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
